/**
 * 套图模板主图 / 细节图层级与颜色元数据
 */

import { stripImageFileExtension } from '@/utils/imageUrlUtils';
import { designerText, designerTextTpl } from '@/i18n/designerText';

export const TEMPLATE_IMAGE_ROLE_MAIN = 'main';
export const TEMPLATE_IMAGE_ROLE_DETAIL = 'detail';

/**
 * @deprecated 设计模板颜色已改为颜色库；保留空数组仅作兼容旧引用。
 * 请使用 useDesignColorLibrary / officialDataAPI.listDesignColors。
 */
export const TEMPLATE_MAIN_COLOR_OPTIONS = [];

export class TemplateValidationError extends Error {
  constructor(message, { missingColorMainIds = [] } = {}) {
    super(message);
    this.name = 'TemplateValidationError';
    this.missingColorMainIds = missingColorMainIds;
  }
}

export function collectUsedTemplateColorCodes(slides = [], excludeMainId = '') {
  const codes = new Set();
  (Array.isArray(slides) ? slides : [])
    .filter(isMainTemplateSlide)
    .forEach((slide) => {
      if (excludeMainId && slide.id === excludeMainId) return;
      const code = String(slide.colorCode || '').trim();
      if (code) codes.add(code);
    });
  return codes;
}

/**
 * 仅从颜色库选项解析颜色；不再支持自定义输入生成编码。
 * @param {string} label
 * @param {Set<string>} usedCodes
 * @param {Array<{ colorLabel: string, colorCode: string }>} options
 */
export function resolveTemplateColorFromInput(label, usedCodes = new Set(), options = [], flags = {}) {
  const trimmed = String(label || '').trim();
  if (!trimmed) return null;
  const allowCustom = Boolean(flags?.allowCustom);

  const list = Array.isArray(options) ? options : [];
  const preset = list.find((item) => item.colorLabel === trimmed)
    || list.find((item) => String(item.colorCode || '').trim() === trimmed);
  if (!preset) {
    if (!allowCustom) return null;
    // 自定义颜色：仅有颜色名，无颜色编码
    return {
      colorLabel: trimmed,
      colorCode: '',
      isCustom: true,
    };
  }
  if (usedCodes.has(preset.colorCode)) {
    return null;
  }
  return {
    colorLabel: String(preset.colorLabel || '').trim(),
    colorCode: String(preset.colorCode || '').trim(),
    isCustom: Boolean(preset.isCustom),
  };
}

export function normalizeTemplateImageRole(role) {
  return role === TEMPLATE_IMAGE_ROLE_DETAIL
    ? TEMPLATE_IMAGE_ROLE_DETAIL
    : TEMPLATE_IMAGE_ROLE_MAIN;
}

/**
 * 从 images 元数据或 slide 对象解析层级字段（旧模板默认全部主图）
 */
export function enrichSlideHierarchyFields(slide, meta = null) {
  const roleSource = meta?.role ?? slide?.role ?? slide?.imageKind;
  const role = normalizeTemplateImageRole(roleSource);
  const parentMainId = role === TEMPLATE_IMAGE_ROLE_DETAIL
    ? String(meta?.parentMainId ?? slide?.parentMainId ?? slide?.parentImageId ?? '').trim() || null
    : null;
  return {
    ...slide,
    role,
    parentMainId,
    colorLabel: role === TEMPLATE_IMAGE_ROLE_MAIN
      ? String(meta?.colorLabel ?? slide?.colorLabel ?? '').trim()
      : '',
    colorCode: role === TEMPLATE_IMAGE_ROLE_MAIN
      ? String(meta?.colorCode ?? slide?.colorCode ?? '').trim()
      : '',
  };
}

export function isMainTemplateSlide(slide) {
  return normalizeTemplateImageRole(slide?.role) === TEMPLATE_IMAGE_ROLE_MAIN;
}

export function isDetailTemplateSlide(slide) {
  return normalizeTemplateImageRole(slide?.role) === TEMPLATE_IMAGE_ROLE_DETAIL;
}

/** 将扁平 slides 按主图分组（主图顺序保持） */
export function groupSlidesByMain(slides = []) {
  const list = Array.isArray(slides) ? slides : [];
  const mains = list.filter(isMainTemplateSlide);
  const detailsByParent = new Map();
  list.filter(isDetailTemplateSlide).forEach((slide) => {
    const pid = String(slide.parentMainId || '').trim();
    if (!pid) return;
    if (!detailsByParent.has(pid)) detailsByParent.set(pid, []);
    detailsByParent.get(pid).push(slide);
  });
  return mains.map((main, index) => ({
    main,
    mainIndex: index,
    details: detailsByParent.get(main.id) || [],
  }));
}

export function buildMainSlideName(index) {
  return `主图${index}`;
}

export function buildDetailSlideName(index) {
  return `细节图${index}`;
}

/** 胶片条等展示用：默认主图/细节图名走词条，自定义名原样（无词条则不变）。 */
export function localizeTemplateSlideName(name) {
  const raw = String(name || '').trim();
  if (!raw) return raw;
  const main = raw.match(/^主图(\d+)$/);
  if (main) return designerTextTpl('主图{0}', main[1]);
  const detail = raw.match(/^细节图(\d+)$/);
  if (detail) return designerTextTpl('细节图{0}', detail[1]);
  return designerText(raw);
}

/** 从图片元数据解析展示名称（优先原始文件名，去掉扩展名） */
export function resolveImageMetaDisplayName(meta, fallback = '') {
  const fromName = stripImageFileExtension(
    meta?.name || meta?.fileName || meta?.originalName || '',
  );
  if (fromName) return fromName;

  const src = String(meta?.src || meta?.imageUrl || meta?.thumbUrl || meta?.url || '');
  if (src) {
    const pathPart = src.split('?')[0].split('/').pop() || '';
    const fromPath = stripImageFileExtension(pathPart);
    if (fromPath) return fromPath;
  }

  return String(fallback || '').trim();
}

/**
 * 保留已有名称，仅在缺失时补默认占位名（不再强制主图1、细节图1编号）
 */
export function reindexTemplateSlideNames(slides = []) {
  return slides.map((slide) => {
    const name = String(slide.name || '').trim();
    if (name) return slide;
    if (isMainTemplateSlide(slide)) {
      return { ...slide, name: '未命名主图' };
    }
    return { ...slide, name: '未命名细节图' };
  });
}

export function createDefaultMainSlideFields() {
  return {
    role: TEMPLATE_IMAGE_ROLE_MAIN,
    parentMainId: null,
    colorLabel: '',
    colorCode: '',
  };
}

export function createDefaultDetailSlideFields(parentMainId) {
  return {
    role: TEMPLATE_IMAGE_ROLE_DETAIL,
    parentMainId: String(parentMainId || '').trim() || null,
    colorLabel: '',
    colorCode: '',
  };
}

/**
 * 保存前校验：每个主图必须有唯一颜色；主图数 = 颜色数
 * @returns {{ ok: boolean, message?: string, missingColorMainIds?: string[] }}
 */
export function validateTemplateSlideHierarchy(slides = []) {
  const list = Array.isArray(slides) ? slides : [];
  if (!list.length) {
    return { ok: false, message: designerText('请至少添加一张主图'), missingColorMainIds: [] };
  }

  const mains = list.filter(isMainTemplateSlide);
  if (!mains.length) {
    return { ok: false, message: designerText('请至少保留一张主图'), missingColorMainIds: [] };
  }

  const codes = new Set();
  const missingColorMainIds = [];
  const duplicateMessages = [];

  mains.forEach((main) => {
    const label = String(main.colorLabel || '').trim();
    const code = String(main.colorCode || '').trim();
    if (!label || !code) {
      missingColorMainIds.push(main.id);
      return;
    }
    if (codes.has(code)) {
      duplicateMessages.push(main.name || main.id);
      return;
    }
    codes.add(code);
  });

  if (missingColorMainIds.length) {
    return {
      ok: false,
      message: designerText('请为每张主图选择颜色'),
      missingColorMainIds,
    };
  }

  if (duplicateMessages.length) {
    const listed = duplicateMessages.slice(0, 3).join('、');
    const more = duplicateMessages.length > 3 ? '…' : '';
    return {
      ok: false,
      message: designerTextTpl('颜色不能重复：{0}{1}', listed, more),
      missingColorMainIds: [],
    };
  }

  if (codes.size !== mains.length) {
    return {
      ok: false,
      message: designerText('主图数量与颜色数量不一致，请为每张主图选择唯一颜色'),
      missingColorMainIds: [],
    };
  }

  const mainIds = new Set(mains.map((m) => m.id));
  const orphanDetails = list.filter(
    (s) => isDetailTemplateSlide(s) && !mainIds.has(String(s.parentMainId || '')),
  );
  if (orphanDetails.length) {
    return {
      ok: false,
      message: designerText('存在未关联主图的细节图，请删除或重新关联'),
      missingColorMainIds: [],
    };
  }

  return { ok: true };
}

export function slidesToTemplateImagesMeta(slides = []) {
  return (Array.isArray(slides) ? slides : []).map((slide) => ({
    id: slide.id,
    name: slide.name,
    width: slide.width,
    height: slide.height,
    role: normalizeTemplateImageRole(slide.role),
    parentMainId: isDetailTemplateSlide(slide) ? slide.parentMainId : undefined,
    colorLabel: isMainTemplateSlide(slide) ? (slide.colorLabel || undefined) : undefined,
    colorCode: isMainTemplateSlide(slide) ? (slide.colorCode || undefined) : undefined,
  }));
}

export function mergeImagesMetaIntoSlides(slides = [], imagesMeta = []) {
  const metaById = new Map((imagesMeta || []).map((m) => [m.id, m]));
  return slides.map((slide) => {
    const meta = metaById.get(slide.id);
    const enriched = enrichSlideHierarchyFields(slide, meta);
    const metaName = String(meta?.name || '').trim();
    if (metaName) {
      return { ...enriched, name: metaName };
    }
    return enriched;
  });
}
